/**
 * Trục thời gian của Gantt: ngày lịch ↔ toạ độ x.
 *
 * Không dùng d3-scale hay visx: phép biến đổi ở đây là một hàm tuyến tính từ
 * "số ngày kể từ mốc trái" sang pixel. Kéo một thư viện scale vào để làm một
 * phép nhân là thêm một lớp che khuất đúng chỗ dễ sai nhất — và chỗ dễ sai đó
 * (làm tròn khi kéo thanh, tuần bắt đầu thứ Hai) thì thư viện nào cũng không lo hộ.
 *
 * Mọi phép tính theo **UTC**. Ngày lịch trong hệ thống là `YYYY-MM-DD` không
 * kèm múi giờ; dùng giờ địa phương ở đây thì một người ở UTC+7 mở Gantt lúc
 * 7 giờ sáng sẽ thấy thanh lệch một ô.
 */

export type Zoom = 'day' | 'week' | 'month';

/** Bề rộng một ngày, theo mức phóng. Đủ để chữ trên nhãn không chồng nhau. */
export const DAY_WIDTH: Record<Zoom, number> = { day: 36, week: 12, month: 4 };

export function parseDate(value: string): Date {
  return new Date(`${value}T00:00:00Z`);
}

export function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function dayDiff(from: Date, to: Date): number {
  return Math.round((startOfDay(to) - startOfDay(from)) / 86_400_000);
}

export function addDays(date: Date, days: number): Date {
  return new Date(startOfDay(date) + days * 86_400_000);
}

export function startOfDay(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export interface Scale {
  readonly origin: Date;
  readonly dayWidth: number;
  readonly width: number;
  readonly days: number;
}

/**
 * Trục cho khoảng `[start, end]`, đệm thêm mỗi bên vài ngày.
 *
 * Đệm để thanh đầu và thanh cuối không dính sát mép — và để kéo dài một thanh
 * ra ngoài khoảng hiện tại không bị chặn bởi chính cái biên đang vẽ.
 */
/**
 * Nới khung thời gian để bao cả những mốc nằm ngoài vùng task — APM-TEST-h.
 *
 * `windowStart`/`windowEnd` do server suy ra từ **ngày của các task** (xem
 * `timeWindow` ở `gantt.service.ts`), nên một mốc đứng ngoài vùng ấy sẽ được
 * `dateToX` đặt vào toạ độ âm hoặc quá `scale.width` — tức vẽ ra ngoài vùng
 * cuộn, và người dùng thấy một biểu đồ **thiếu vạch** chứ không thấy lỗi.
 *
 * Đúng trường hợp hay xảy ra nhất: vòng test cuối diễn ra sau khi mọi task đã
 * hết hạn, nên nó luôn nằm ngoài khung nếu không nới.
 */
export function widenWindow(
  start: Date,
  end: Date,
  dates: readonly Date[],
): { readonly start: Date; readonly end: Date } {
  const stamps = dates.map(startOfDay);

  return {
    start: new Date(Math.min(startOfDay(start), ...stamps)),
    end: new Date(Math.max(startOfDay(end), ...stamps)),
  };
}

export function createScale(start: Date, end: Date, zoom: Zoom, padDays = 3): Scale {
  const origin = addDays(start, -padDays);
  const days = Math.max(dayDiff(origin, end) + padDays + 1, 1);
  const dayWidth = DAY_WIDTH[zoom];

  return { origin, dayWidth, days, width: days * dayWidth };
}

export function dateToX(scale: Scale, date: Date): number {
  return dayDiff(scale.origin, date) * scale.dayWidth;
}

/**
 * Toạ độ x ngược về ngày, **làm tròn về ô ngày gần nhất**.
 *
 * Bắt dính vào ngày chứ không cho ngày lẻ theo pixel: kéo thả là thao tác thô,
 * và một thanh kết thúc lúc "14 giờ 37" không có nghĩa gì với `DATE` trong DB.
 * Đây cũng là thứ khiến người dùng không phải gõ ngày bao giờ.
 */
export function xToDate(scale: Scale, x: number): Date {
  return addDays(scale.origin, Math.round(x / scale.dayWidth));
}

export interface Tick {
  readonly date: Date;
  readonly x: number;
  readonly label: string;
  /** Mốc chính (đầu tháng, hoặc thứ Hai) — vẽ vạch đậm hơn. */
  readonly major: boolean;
}

const MONTHS = [
  'Th1',
  'Th2',
  'Th3',
  'Th4',
  'Th5',
  'Th6',
  'Th7',
  'Th8',
  'Th9',
  'Th10',
  'Th11',
  'Th12',
];

/**
 * Vạch chia trục, thưa dần theo mức phóng.
 *
 * `day` mỗi ngày một vạch, `week` mỗi thứ Hai, `month` mỗi mùng 1. Vẽ mỗi ngày
 * một vạch ở mức `month` thì một dự án một năm có 365 nhãn chồng lên nhau.
 */
export function ticks(scale: Scale, zoom: Zoom): Tick[] {
  const result: Tick[] = [];

  for (let day = 0; day < scale.days; day++) {
    const date = addDays(scale.origin, day);
    const weekday = date.getUTCDay();
    const dayOfMonth = date.getUTCDate();

    const show =
      zoom === 'day' ||
      (zoom === 'week' && weekday === 1) ||
      (zoom === 'month' && dayOfMonth === 1);
    if (!show) continue;

    result.push({
      date,
      x: day * scale.dayWidth,
      label:
        zoom === 'month'
          ? `${MONTHS[date.getUTCMonth()]!}`
          : `${dayOfMonth}/${date.getUTCMonth() + 1}`,
      major: dayOfMonth === 1 || (zoom === 'day' && weekday === 1),
    });
  }

  return result;
}

/** Cuối tuần, để tô nền xám — người đọc nhận ra nhịp tuần mà không cần nhãn. */
export function weekendBands(scale: Scale): { x: number; width: number }[] {
  const bands: { x: number; width: number }[] = [];

  for (let day = 0; day < scale.days; day++) {
    const weekday = addDays(scale.origin, day).getUTCDay();
    if (weekday !== 0 && weekday !== 6) continue;
    bands.push({ x: day * scale.dayWidth, width: scale.dayWidth });
  }

  return bands;
}

/**
 * Hàng nào cần vẽ, xét khung nhìn đang cuộn tới đâu.
 *
 * Gantt một dự án lớn có vài nghìn hàng (docs/frontend-guide.md §5). Không cắt
 * bớt thì mỗi lần kéo thanh, trình duyệt phải bố trí lại toàn bộ.
 */
export function visibleRows(
  scrollTop: number,
  viewportHeight: number,
  rowHeight: number,
  total: number,
  overscan = 5,
): { from: number; to: number } {
  const from = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const to = Math.min(total, Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan);
  return { from, to };
}
